import Input from 'components/Input/Input'
import { useTranslation } from 'react-i18next'
import { ORDER_STATUS } from 'types/api/order'
import RoomIcon from '@mui/icons-material/Room'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { usePlaces } from 'providers/PlacesProvider'
import { useEffect } from 'react'
import { useRoutePlanning } from 'providers/RoutePlanningProvider'
import { generateCrc } from 'lib/utils'
import { isEqual, uniq } from 'lodash'
import { format } from 'date-fns'
import { useMutateUpdateDeliveryShipments } from 'hooks/useDeliveries'
import { PlaceApi } from 'types/api/place'
import {
  Button,
  CircularProgress,
  Typography,
  Divider,
  Box,
  useMediaQuery,
} from '@mui/material'
import IconDropdown from 'components/IconDropdown/IconDropdown'
import { useLocale } from 'providers/LocaleProvider'
import { useQueryClient } from 'react-query'
import { useFormikContext } from 'formik'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import TagManager from 'react-gtm-module'
import { TRACKING_EVENT } from 'types/model/delivery'
import DraggableListItem from './DraggableListItem'
import { DottedLine, OrderItem } from './elements'
import ShipmentOrdersList from './ShipmentOrdersList'
import { RoutePlanningFormProps } from './OrderListing'

const RouteOrdersView = () => {
  const { selectedLocale } = useLocale()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { selectedPlace } = usePlaces()
  const { setAlert } = useAlert()

  const {
    ordersFromApi,
    deliveriesFromApi,
    ordersToDisplay,
    setOrdersToDisplay,
    shipmentsInOrder,
    setShipmentsInOrder,
  } = useRoutePlanning()

  const orderIsInRouteOrBeyond =
    ordersToDisplay[0].orderStatus === ORDER_STATUS.DELIVERED ||
    ordersToDisplay[0].orderStatus === ORDER_STATUS.DELIVERING

  const { mutate, isLoading } = useMutateUpdateDeliveryShipments(
    (selectedPlace as PlaceApi).masterId
  )
  const queryClient = useQueryClient()
  const { dirty, values } = useFormikContext<RoutePlanningFormProps>()

  useEffect(() => {
    setShipmentsInOrder(ordersToDisplay)
  }, [ordersToDisplay])

  const handleShipmentEdit = (action: string) => {
    const eventVariables = {
      placeId: selectedPlace?.masterId || '',
      placeName: selectedPlace?.name || '',
      organizationId: selectedPlace?.organizationId || '',
    }
    if (dirty === true) {
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.CHANGE_ROUTE_START_TIME,
          ...eventVariables,
        },
      })
    }
    if (action === 'deleteRoute') {
      TagManager.dataLayer({
        dataLayer: {
          event: TRACKING_EVENT.DELETE_ROUTE,
          ...eventVariables,
        },
      })
    }
    const shipmentOrder = shipmentsInOrder.map((order, index) => ({
      id: Number(order.parts[0].id),
      seq: index,
    }))
    const deliveryIds = shipmentOrder
      .map((data) => {
        const orderdata = ordersFromApi?.filter(
          (order) => Number(order.parts[0].id) === Number(data.id)
        )
        if (orderdata) {
          return Number(orderdata[0].parts[0].deliveryId)
        }
        return NaN
      })
      .filter(Boolean)

    const uniqueDeliveryIds = uniq(deliveryIds).filter(Boolean)

    const crcsWithIds = generateCrc(uniqueDeliveryIds, deliveriesFromApi) as {
      crc: string
      deliveryId: number
    }[]

    const deliveryIdsinOrder = crcsWithIds.map((item) => item.deliveryId)
    const crcsInOrder = crcsWithIds.map((item) => item.crc)
    const { deliveryDate } = ordersToDisplay[0].orderDeliveryTimes
    const hours = Number(values.startTime?.split(':')[0])
    const minutes = Number(values.startTime?.split(':')[1])
    const date = new Date(deliveryDate)
    date.setHours(hours, minutes)

    const datePayload = format(date, 'yyyy-MM-dd HH:mm:ss xxx')

    const getPayload = (action: string) => {
      return {
        shipments: [
          {
            group_index: action?.includes('deleteRoute')
              ? -1
              : shipmentsInOrder[0].shipmentData?.[0].groupIndex,
            ...(action?.indexOf('deleteRoute') === -1 && {
              start_time: datePayload,
            }),
            orders: shipmentOrder,
          },
        ],
        delivery_crcs: crcsInOrder,
        delivery_ids: deliveryIdsinOrder,
        message: 'edit route',
      }
    }
    mutate(getPayload(action), {
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onSuccess: () => {
        setOrdersToDisplay(shipmentsInOrder)
        queryClient.refetchQueries(['deliveries'])
        queryClient.refetchQueries(['ordersForPlace'])
      },
    })
  }

  const dropdownOptions = [{ label: t('deleteRoute'), value: 'deleteRoute' }]

  const driverPositionExists =
    (shipmentsInOrder.some(
      (order) => order.parts[0].status === ORDER_STATUS.DELIVERED
    ) &&
      !shipmentsInOrder.every(
        (order) => order.parts[0].status === ORDER_STATUS.DELIVERED
      )) ||
    shipmentsInOrder.some(
      (order) => order.parts[0].status === ORDER_STATUS.DELIVERING
    )

  return (
    <Box sx={{ position: 'relative' }}>
      <Box pt={1} pl={3} pb={3}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h2' color='commonColors.black' fontWeight='bold'>
            {format(
              new Date(
                ordersToDisplay[0].shipmentDeliveryTimes?.deliveryDate ||
                  new Date()
              ),
              'd.M.yy',
              { locale: selectedLocale }
            )}{' '}
            - {t('routePlanning.routeName')} {ordersToDisplay[0].routeName}
          </Typography>
          {shipmentsInOrder.some(
            (order) => order.orderStatus === ORDER_STATUS.COLLECTED
          ) && (
            <Box mr={2}>
              <IconDropdown
                minWidth='150px'
                height='222px'
                isIconOnly
                ariaLabel={t('routePlanning.actionMenu')}
                onChange={(_, value) => handleShipmentEdit(value)}
                options={dropdownOptions}
                Icon={MoreVertIcon}
              />
            </Box>
          )}
        </Box>
        <Box mt={5}>
          <Input
            name='startTime'
            type='text'
            label={t('routePlanning.departureTimeLabel')}
            variant='filled'
            fullWidth
            disabled={orderIsInRouteOrBeyond}
          />
        </Box>
      </Box>
      <Divider variant='middle' />
      <Box mb={6} position='relative'>
        {orderIsInRouteOrBeyond ? (
          <DottedLine
            length={ordersToDisplay.length}
            driverPositionExists={driverPositionExists ? '32px' : '0px'}
            orderColor={ordersToDisplay[0].color}
          />
        ) : null}
        <OrderItem
          hideBorders={orderIsInRouteOrBeyond}
          defaultCursor
          display='flex'
          alignItems='center'
          ml={orderIsInRouteOrBeyond ? 0 : 0.7}
          smallPadding={orderIsInRouteOrBeyond}
          color='commonColors.black'>
          <Typography variant='body2' pr={orderIsInRouteOrBeyond ? 3.5 : 0.5}>
            {ordersToDisplay[0].shipmentDeliveryTimes?.shipmentStartTime}
          </Typography>
          <RoomIcon sx={{ fill: 'commonColors.black' }} />
          <Typography pl={1.5} variant='body2'>
            {selectedPlace?.name}
          </Typography>
        </OrderItem>
        {orderIsInRouteOrBeyond ? (
          <ShipmentOrdersList orders={ordersToDisplay} />
        ) : (
          <DraggableListItem setShipmentsInOrder={setShipmentsInOrder} />
        )}
      </Box>

      {!orderIsInRouteOrBeyond && (
        <Box
          sx={{
            position: 'fixed',
            height: '60px',
            bottom: 0,
            width: '410px',
            bgcolor: 'commonColors.white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Button
            id={TRACKING_EVENT.SAVING_ROUTE_CHANGES}
            sx={{ width: '80%' }}
            onClick={() => handleShipmentEdit('editRoute')}
            disabled={
              !(dirty || !isEqual(ordersToDisplay, shipmentsInOrder)) ||
              isLoading
            }>
            {isLoading ? (
              <CircularProgress
                size='20px'
                sx={{ color: theme.palette.commonColors.white }}
              />
            ) : (
              t('save')
            )}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default RouteOrdersView
